// src/config/ccpFlowLabels.js
// Display text for the CCP production module, keyed by BakingCcpConfig.flowType. Backend status
// values and internal route/module names stay the same for every company (see
// backend/routes/bakingCcpRoutes.js FLOW_STAGES) — only what's shown on screen changes.
//
// Usage: const L = getCcpFlowLabels(flowType); ... t(L.stage1Verb.en, L.stage1Verb.es, lang)

const BAKING_LABELS = {
  pageTitleLive: { en: 'Baking CCP — Live Mode', es: 'CCP de Horneado — Modo en Vivo' },
  pageTitleStart: { en: 'Baking CCP — Start Production', es: 'CCP de Horneado — Iniciar Producción' },
  stage1Verb: { en: 'BAKING', es: 'HORNEADO' },
  stage1Action: { en: 'Baking', es: 'Horneado' },
  stopStage1Button: { en: 'Stop Baking', es: 'Detener Horneado' },
  stopStage1Notice: { en: 'Baking stopped. Packaging can continue with freezer events and completion.', es: 'Horneado detenido. El empaque puede continuar con eventos de congelador y completar la corrida.' },
  vessel: { en: 'Oven', es: 'Horno' },
  vesselTempLabel: { en: 'Oven Temperature at Start (°F) — Required', es: 'Temperatura del Horno al Inicio (°F) — Requerido' },
  vesselTempPlaceholder: 'e.g. 900',
  vesselTempQuick: [200, 205, 210, 215, 220],
  vesselTempRangeMinPlaceholder: 'e.g. 850',
  vesselTempRangeMaxPlaceholder: 'e.g. 950',
  vesselOutLabel: { en: 'Oven Out', es: 'Salida Horno' },
  unit: { en: 'Cart', es: 'Carrito' },
  unitPlural: { en: 'Carts', es: 'Carritos' },
  newUnitButton: { en: 'NEW CART', es: 'NUEVO CARRITO' },
  blastInLabel: { en: 'Blast In', es: 'Entrada Congelador' },
  blastOutLabel: { en: 'Blast Out', es: 'Salida Congelador' },
  requirementStatus: { en: 'BAKING_STOPPED', es: 'BAKING_STOPPED' },
  machineLabel: { en: 'freezer', es: 'congelador' },
  cycleLabel: { en: 'blast freezer', es: 'congelador' },
  stage1Gerund: { en: 'baking', es: 'horneando' },
  intervalCheckNote: { en: 'Periodic oven temperature checks while baking.', es: 'Controles periódicos de temperatura del horno durante el horneado.' },
};

const PASTEURIZATION_LABELS = {
  pageTitleLive: { en: 'Pasteurization CCP — Live Mode', es: 'CCP de Pasteurización — Modo en Vivo' },
  pageTitleStart: { en: 'Pasteurization CCP — Start Production', es: 'CCP de Pasteurización — Iniciar Producción' },
  stage1Verb: { en: 'PASTEURIZING', es: 'PASTEURIZANDO' },
  stage1Action: { en: 'Pasteurization', es: 'Pasteurización' },
  stopStage1Button: { en: 'Stop Pasteurizing', es: 'Detener Pasteurización' },
  stopStage1Notice: { en: 'Pasteurization stopped. Aging has started.', es: 'Pasteurización detenida. Comenzó el añejamiento.' },
  vessel: { en: 'Pasteurizer', es: 'Pasteurizador' },
  // This is the MIX/PRODUCT temperature, not the pasteurizer's jacket/water-bath temperature —
  // that's what the CCP kill-step actually depends on. Confirm the exact required time/temp
  // against your validated HACCP plan; ~155°F/30min (vat) is a common starting point, not a rule.
  vesselTempLabel: { en: 'Mix Temperature at Pasteurization (°F) — Required', es: 'Temperatura de la Mezcla en Pasteurización (°F) — Requerido' },
  vesselTempPlaceholder: 'e.g. 155',
  vesselTempQuick: [150, 155, 160, 165, 175],
  vesselTempRangeMinPlaceholder: 'e.g. 150',
  vesselTempRangeMaxPlaceholder: 'e.g. 175',
  vesselOutLabel: { en: 'Pasteurizer Out', es: 'Salida Pasteurizador' },
  unit: { en: 'Batch', es: 'Lote' },
  unitPlural: { en: 'Batches', es: 'Lotes' },
  newUnitButton: { en: 'NEW BATCH', es: 'NUEVO LOTE' },
  blastInLabel: { en: 'Start Churning', es: 'Iniciar Batido' },
  blastOutLabel: { en: 'Stop Churning', es: 'Detener Batido' },
  requirementStatus: { en: 'READY_TO_CHURN', es: 'READY_TO_CHURN' },
  // Pasteurization-only — the aging hold between stopping pasteurization and churning.
  agingLabel: { en: 'Aging', es: 'Añejamiento' },
  agingCardTitle: { en: 'Aging Hold', es: 'Reposo de Añejamiento' },
  agingInProgress: { en: 'Base is aging. Churn batches can be created once aging ends.', es: 'La base está añejando. Los lotes de batido se pueden crear cuando termine el añejamiento.' },
  endAgingButton: { en: 'End Aging — Ready to Churn', es: 'Terminar Añejamiento — Listo para Batir' },
  readyToChurnLabel: { en: 'Ready to Churn', es: 'Listo para Batir' },
  machineLabel: { en: 'churner', es: 'batidora' },
  cycleLabel: { en: 'churning', es: 'batido' },
  // Required alongside vesselTempLabel (start-of-hold) — captured when "Stop Pasteurizing" is
  // clicked, so there's a temperature reading at both ends of the hold, not just the start.
  finalTempLabel: { en: 'Final Mix Temperature at Stop (°F) — Required', es: 'Temperatura Final de la Mezcla al Detener (°F) — Requerido' },
  stage1Gerund: { en: 'pasteurizing', es: 'pasteurizando' },
  // Distinguishes the periodic in-hold checks from the required start/stop bookend readings —
  // both matter: bookends prove the hold started/ended at temp, intervals prove it didn't dip
  // in between. No batches exist yet at this stage (they're created after aging ends), so there's
  // nothing to "tie" a reading to — unlike baking, where carts exist throughout.
  intervalCheckNote: { en: 'Periodic mix-temperature checks during the hold, in addition to the required start and stop readings.', es: 'Controles periódicos de la temperatura de la mezcla durante el sostenimiento, además de las lecturas requeridas de inicio y detención.' },
};

const FLOW_LABELS = {
  baking: BAKING_LABELS,
  pasteurization: PASTEURIZATION_LABELS,
};

export function getCcpFlowLabels(flowType) {
  return FLOW_LABELS[flowType] || BAKING_LABELS;
}

export default getCcpFlowLabels;
